import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// 환경별 설정
const env = {
  production: {
    AUTH_BASE_URL: 'https://attendance.icoramdeo.com/auth',
    API_BASE_URL: 'https://attendance.icoramdeo.com/api',
    ADMIN_CONTACT: 'admin@example.com',
  },
  development: {
    AUTH_BASE_URL: 'https://attendance-dev.icoramdeo.com/auth',
    API_BASE_URL: 'https://attendance-dev.icoramdeo.com/api', // 개발 서버 API 사용
    ADMIN_CONTACT: 'admin@example.com',
  },
  local: {
    AUTH_BASE_URL: 'http://localhost:3000/auth',
    API_BASE_URL: 'http://localhost:3000/api',
    ADMIN_CONTACT: 'admin@example.com',
  },
};

// 현재 환경 결정
// 런타임 환경 변수 (window.__ENV__)를 우선적으로 사용
// 없으면 빌드 타임 환경 변수 (process.env) 사용
const getCurrentEnv = () => {
  // 런타임 환경 변수 확인 (Docker 컨테이너에서 주입됨)
  const runtimeEnv = (window as any).__ENV__;
  const nodeEnv = runtimeEnv?.NODE_ENV || process.env.NODE_ENV;
  const reactAppEnv = runtimeEnv?.REACT_APP_ENV || process.env.REACT_APP_ENV;

  if (nodeEnv === 'production') {
    return 'production';
  } else if (reactAppEnv === 'local') {
    return 'local';
  } else {
    return 'development'; // 기본값을 development로 변경
  }
};

const currentEnv = getCurrentEnv();
const config = env[currentEnv];

// API 기본 URL 설정
const API_BASE_URL = config.API_BASE_URL;
const AUTH_BASE_URL = config.AUTH_BASE_URL;

// API용 Axios 인스턴스 생성
const axiosClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth용 Axios 인스턴스 생성
const authClient: AxiosInstance = axios.create({
  baseURL: AUTH_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** POST, PUT, PATCH 요청에 Content-Type 헤더 보장이 필요한 메서드 목록 */
const METHODS_REQUIRING_CONTENT_TYPE = ['post', 'put', 'patch'];

// 공통 요청 인터셉터 함수
const addRequestInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // authUtils에서 토큰 가져오기
      const { getAccessToken } = await import('./authUtils');
      const token = getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // POST, PUT, PATCH 요청에 Content-Type: application/json 명시적 보장
      // 인스턴스 기본값이 있더라도 개별 요청에서 누락될 수 있으므로 인터셉터에서 강제 적용
      if (config.method && METHODS_REQUIRING_CONTENT_TYPE.includes(config.method.toLowerCase())) {
        if (config.headers && !config.headers['Content-Type']) {
          config.headers['Content-Type'] = 'application/json';
        }
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );
};

// 공통 응답 인터셉터 함수
const addResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async error => {
      // 401 에러 시 토큰 새로고침 시도
      if (error.response?.status === 401) {
        const { clearAuthData } = await import('./authUtils');
        clearAuthData();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// 인터셉터 적용
addRequestInterceptor(axiosClient);
addResponseInterceptor(axiosClient);
addRequestInterceptor(authClient);
addResponseInterceptor(authClient);

// 환경 정보와 함께 export
export { authClient, config, currentEnv };
export default axiosClient;
